/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck
/**
 * OnboardingModal — first-visit explainer for any view.
 *
 * Each view passes a unique `viewKey` (e.g. "mapview", "dropoff",
 * "canvass") plus a list of glossary term ids that are most worth
 * understanding before using that view. The first time a user opens that
 * view in this browser, the modal pops automatically. Dismissal is stored
 * in localStorage under `pcgop.onboard.<viewKey>` so it doesn't pop again.
 *
 * Re-opening: render an <OnboardingHelpButton viewKey="mapview" /> in the
 * view header. It bumps the modal back open ignoring the localStorage flag.
 *
 * The modal is intentionally simple — title, "what this view shows" body,
 * a 3-5 entry term cheat-sheet pulling from the glossary, an optional list
 * of "common workflows" bullets, and two buttons (Got it / Open glossary).
 */
import React, { useEffect, useState } from 'react';
import { GLOSSARY, type GlossaryTerm } from '@/lib/glossary';

const STORAGE_PREFIX = 'pcgop.onboard.';

type Props = {
  viewKey: string;
  title: string;
  /** One-paragraph plain English description of what this view shows. */
  intro: string;
  /** Glossary ids surfaced as a cheat-sheet. */
  termIds: string[];
  /** Optional bullet list of common workflows / "you might want to..." */
  workflows?: string[];
  /** Override automatic open — used by the help button. */
  forceOpen?: boolean;
  /** Called when the modal closes. Lets the help button reset state. */
  onClose?: () => void;
};

function storageKeyFor(viewKey: string) {
  return `${STORAGE_PREFIX}${viewKey}`;
}

export function hasSeenOnboarding(viewKey: string): boolean {
  if (typeof window === 'undefined') return true;
  try {
    return window.localStorage.getItem(storageKeyFor(viewKey)) === '1';
  } catch {
    return true;
  }
}

function markSeen(viewKey: string) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKeyFor(viewKey), '1');
  } catch {
    // ignore — Safari private mode etc.
  }
}

function dispatchNavigateToGlossary() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('infotip:navigate', { detail: { termId: '' } }));
}

export const OnboardingModal: React.FC<Props> = ({
  viewKey,
  title,
  intro,
  termIds,
  workflows,
  forceOpen,
  onClose,
}) => {
  // Test/dev escape hatch — allows smoke harness to disable overlays without
  // localStorage seeding. Also honors a runtime window flag so a harness
  // targeting an already-running dev server can set it via addInitScript.
  if (
    typeof window !== 'undefined' &&
    ((window as any).__PCGOP_DISABLE_ONBOARDING__ === true ||
      (import.meta as any).env?.VITE_DISABLE_ONBOARDING === '1')
  ) {
    return null;
  }

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (forceOpen) {
      setOpen(true);
      return;
    }
    if (!hasSeenOnboarding(viewKey)) setOpen(true);
  }, [viewKey, forceOpen]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function handleClose() {
    markSeen(viewKey);
    setOpen(false);
    onClose?.();
  }

  if (!open) return null;

  const terms: GlossaryTerm[] = termIds
    .map((id) => GLOSSARY[id])
    .filter(Boolean) as GlossaryTerm[];

  return (
    <div
      className="fixed inset-0 z-[9000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={handleClose}
    >
      <div
        className="bg-bg-base border border-border-base rounded-xl shadow-ds-xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex items-stretch"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Brand stripe (left edge) */}
        <div className="w-1.5 sm:w-2 shrink-0 bg-accent rounded-l-xl" aria-hidden="true" />

        {/* Content wrapper */}
        <div className="flex-1 min-w-0 flex flex-col max-h-[85vh] overflow-y-auto">
          {/* Header */}
          <div className="px-6 py-4 border-b border-border-base flex items-start justify-between gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-accent-fg font-semibold mb-1">
                What this view shows
              </div>
              <h2 className="text-xl font-bold text-fg-primary">{title}</h2>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="inline-flex items-center justify-center min-h-11 min-w-11 -mr-2 rounded text-fg-muted hover:text-fg-primary hover:bg-bg-raised transition-colors"
              aria-label="Close"
            >
              <span className="text-2xl leading-none" aria-hidden="true">×</span>
            </button>
          </div>

          {/* Body */}
          <div className="p-6 space-y-5">
            <p className="text-fg-primary leading-relaxed">{intro}</p>

            {terms.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold mb-2">
                  Terms you'll see on this page
                </div>
                <div className="space-y-2">
                  {terms.map((t) => (
                    <div
                      key={t.id}
                      className="rounded-lg border border-border-base bg-bg-raised/60 px-3 py-2"
                    >
                      <div className="text-sm font-semibold text-fg-primary">{t.title}</div>
                      <div className="text-xs text-fg-secondary mt-0.5 leading-snug">
                        {t.short}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {workflows && workflows.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold mb-2">
                  You might want to…
                </div>
                <ul className="space-y-1.5">
                  {workflows.map((w, i) => (
                    <li
                      key={i}
                      className="text-sm text-fg-secondary flex gap-2 leading-snug"
                    >
                      <span className="text-accent-fg mt-0.5">›</span>
                      <span>{w}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-border-base bg-bg-raised/60 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => {
                handleClose();
                dispatchNavigateToGlossary();
              }}
              className="text-sm text-accent-fg hover:opacity-80 font-medium"
            >
              Open full glossary →
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="bg-accent hover:bg-accent-hover text-white font-bold px-4 py-2 rounded-lg text-sm"
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Small (?) button that re-opens the OnboardingModal for a view. Drop it
 * in any view header.
 */
export const OnboardingHelpButton: React.FC<{
  onClick: () => void;
  label?: string;
}> = ({ onClick, label = 'Help' }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center justify-center gap-1 rounded border border-border-base bg-bg-raised hover:bg-bg-elevated hover:border-border-subtle text-fg-secondary hover:text-fg-primary text-xs font-semibold px-2 py-1 min-h-11 min-w-11 sm:min-h-0 sm:min-w-0 transition"
    title="Show the explainer for this view"
  >
    <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-bg-elevated text-[10px] font-bold">
      ?
    </span>
    {label}
  </button>
);

export default OnboardingModal;
